<template>
    <div>
        <vue-google-autocomplete
            v-if="loaded"
            :id="'map'+id"
            class="places"
            classname="form-control"
            :placeholder="placeHolder || $t('profile.type_address')"
            @placechanged="($addressData, $placeResultData, $id) => addressDataHanddler({
                $addressData,
                $id,
                $placeResultData
            })"
            @inputChange="handleInput"
            @blur="handleBlur"
            :value="placeAddress"
            ref="address"
        ></vue-google-autocomplete>
    </div>
</template>

<script>

import { mapState } from 'vuex';
import VueGoogleAutocomplete from "@/components/common/VueGoogleAutocomplete.vue";

export default {
    components: {
        VueGoogleAutocomplete
    },
    props: {
        id: {
            type: [String, Number],
            default: null
        },
        placeHolder: {
            type: String,
            default: ''
        },
        address: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            placeAddress: null,
            error: null,
            loading: true,
            loaded: false,
            loadPlace: false,
            inputHandler: ''
        }
    },
    computed: {
        ...mapState({
            googleApiKey: (state) =>state.env.get('VUE_APP_GOOGLE_MAPS_API_KEY')
        })
    },
    created() {
        this.placeAddress = this.address;
        this.inputHandler = JSON.parse(JSON.stringify(this.address));
        if (this.googleApiKey)
        {
            this.loadGooglePlacesAPI(this.googleApiKey)
                .then(() => {
                    this.loaded = true;
                })
                .catch((error) => {
                    this.error = error.message;
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    },
    mounted()
    {},

    watch: {
        address(val) {
            this.placeAddress = val;
        },
        googleApiKey(newKey)
        {
            if (!newKey)
            {
                return false;
            }

            this.loadGooglePlacesAPI(newKey)
                .then(() => {
                    this.loaded = true;
                })
                .catch((error) => {
                    this.error = error.message;
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    },
    methods: {
        /**
         * Loads the Google Places API dynamically if it's not already loaded, using a global flag.
         *
         * @param {string} apiKey - The Google Maps API key.
         * @returns {Promise} - Resolves when the API is successfully loaded, or if it's already loaded.
         */
        loadGooglePlacesAPI(apiKey) {
            // If Google Maps API is already loaded, resolve immediately
            if (this.isGoogleMapsLoaded()) {
                return Promise.resolve();
            }

            // If the script is already loading, return a resolved/rejected promise based on the result
            if (window.googleMapsScriptLoading) {
                return this.waitForGoogleMaps();
            }

            // Set the global flag indicating the script is loading
            window.googleMapsScriptLoading = true;

            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
                script.async = true;
                script.defer = true;

                // Handle successful loading of the script
                script.onload = () => {
                    window.googleMapsScriptLoading = false;
                    window.googleMapsScriptLoaded = true;
                    resolve();
                };

                // Handle script loading failure
                script.onerror = () => {
                    window.googleMapsScriptLoading = false;
                    window.googleMapsScriptLoaded = 'error';
                    reject(new Error('Failed to load Google Maps API: Invalid API key or network issue.'));
                };

                document.head.appendChild(script);
            });
        },

        /**
         * Checks if the Google Maps API is already loaded.
         *
         * @returns {boolean} - True if Google Maps API is already loaded, otherwise false.
         */
        isGoogleMapsLoaded() {
            return window.google && window.google.maps;
        },

        /**
         * Waits for Google Maps to finish loading, resolving or rejecting based on the outcome.
         *
         * @returns {Promise} - Resolves if Google Maps loads successfully, rejects if it fails.
         */
        waitForGoogleMaps() {
            return new Promise((resolve, reject) => {
                const interval = setInterval(() => {
                    if (this.isGoogleMapsLoaded()) {
                        clearInterval(interval);
                        resolve();
                    } else if (window.googleMapsScriptLoaded === 'error') {
                        clearInterval(interval);
                        reject(new Error('Failed to load Google Maps API.'));
                    }
                }, 100); // Check every 100ms
            });
        },


        setFocus()
        {
            this.$refs.address.focus();
        },
        addressDataHanddler(data) {
            this.loadPlace = true;
            
            let address = data.$addressData.address1;
            const addressComponent = data.$placeResultData.address_components.find(obj => 
                obj.short_name == data.$addressData.administrative_area_level_1
            );
            let state = addressComponent ? addressComponent.long_name : data.$addressData.administrative_area_level_1;
            state = state.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
            
            this.$store.dispatch("setAddressDataAction", {
                address: data.$addressData.address1,
                country: data.$addressData.country,
                city: data.$addressData.locality || data.$addressData.administrative_area_level_2,
                state: state,
                zip: data.$addressData.postal_code || 0,
                updateId: this.id
            });
            
            this.$nextTick(() => {
                this.$refs.address.autocompleteText = address;
            });
            
            setTimeout(() => {
                this.loadPlace = false;
            }, 200);
        },
        handleInput(event) {
            // Si loadPlace es true, salimos temprano de la función
            if (this.loadPlace) return;

            // Limpiamos el valor y verificamos en una sola operación
            const address = event.newVal.trim();
            
            // Si el address está vacío, salimos de la función
            if (!address) return;
            
            // Actualizamos el estado y despachamos la acción
            this.inputHandler = address;
        },
        handleBlur() {
            if (this.loadPlace) return;
            
            this.$store.dispatch('setAddressDataAction', {
                address: this.inputHandler,
                updateId: this.id
            }); 
        },
        updateAddress() {
            if(this.$refs.address) {
                this.$refs.address.loadPlace();               
            }
        }
    },
}
</script>

<style lang="scss">
.map-container
{
    margin-bottom: 2px;
    padding: 5px;
    border: 1px solid #656565;
    border-radius: 5px;

    .map-label
    {
      height: 9px;
      display: block;
      color: rgba(0, 0, 0, 0.6);
      font-size: small;
      margin: 0px;
      padding: 0px;
      line-height: 10px;
    }

    label.map-label {
        margin-top: -10px;
        margin-left: 5px;
        
        span {
            background-color: #fff;
            padding: 2px;
        }
    }

    .map-icon
    {
      font-size: x-large;
      float: right;
      color: #6b6a6a;
      margin-bottom: -53px;
      margin-top: 2px;
      margin-right: 5px;
    }

    input.places {
      width: 100%;
      outline: none;
      padding-right: 45px;
      color: #272727;
    }

    & > div {
        margin-top: 5px;
        margin-left: 5px;
    }
}

.map-container.input-has-error {
    border: 2px solid #ff5252;

    label.map-label {
        color: #ff5252;
    }
}

.map-error-text {
    color: #ff5252;
    font-size: 12px;
}

.map-container.for-profile {
    
    padding-bottom: 0px !important;

    label.map-label {
        margin-left: 5px !important;
    }

    div {
        margin-top: 0px !important;
    }
}
</style>
